import { useState } from "react";
import {
  AGE_GROUPS,
  EMPTY_FILTERS,
  GENDER_OPTIONS,
  LOCATION_OPTIONS,
  PREFECTURES,
} from "../data/constants";
import { activeFilterCount } from "../data/analytics";
import type { FilterState, Gender, LocationKind } from "../types";

type GlobalFiltersProps = {
  filters: FilterState;
  onChange: (filters: FilterState) => void;
  resultCount: number;
  totalCount: number;
};

function ToggleGroup<T extends string | number>({
  values,
  selected,
  onChange,
}: {
  values: Array<{ value: T; label: string }>;
  selected: T[];
  onChange: (values: T[]) => void;
}) {
  return (
    <div className="toggle-list">
      {values.map((option) => {
        const active = selected.includes(option.value);
        return (
          <button
            type="button"
            className={`filter-chip${active ? " is-active" : ""}`}
            aria-pressed={active}
            key={option.value}
            onClick={() =>
              onChange(
                active
                  ? selected.filter((value) => value !== option.value)
                  : [...selected, option.value],
              )
            }
          >
            {active ? "■" : "□"} {option.label}
          </button>
        );
      })}
    </div>
  );
}

export function GlobalFilters({ filters, onChange, resultCount, totalCount }: GlobalFiltersProps) {
  const [expanded, setExpanded] = useState(false);
  const count = activeFilterCount(filters);
  const set = <K extends keyof FilterState>(key: K, value: FilterState[K]) =>
    onChange({ ...filters, [key]: value });

  return (
    <section className="filters" aria-label="全域篩選器">
      <div className="filters__topline">
        <div>
          <p className="eyebrow">GLOBAL FILTER</p>
          <h2>全域篩選</h2>
        </div>
        <div className="filters__status">
          <strong>{resultCount.toLocaleString("zh-Hant-TW")}</strong>
          <span> / {totalCount.toLocaleString("zh-Hant-TW")} 位達標者</span>
        </div>
      </div>

      <div className="filters__primary">
        <label className="field field--grow">
          <span>暱稱關鍵字</span>
          <input
            type="search"
            value={filters.query}
            onChange={(event) => set("query", event.target.value)}
            placeholder="支援全形／半形搜尋"
          />
        </label>
        <button
          className="button button--secondary"
          type="button"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
        >
          {expanded ? "收合條件" : "更多條件"} {count > 0 && <span className="counter">{count}</span>}
        </button>
        <button
          className="button button--ghost"
          type="button"
          onClick={() => onChange({ ...EMPTY_FILTERS })}
          disabled={count === 0}
        >
          清除全部
        </button>
      </div>

      {expanded && (
        <div className="filters__details">
          <fieldset>
            <legend>性別</legend>
            <ToggleGroup<Gender>
              values={GENDER_OPTIONS}
              selected={filters.genders}
              onChange={(values) => set("genders", values)}
            />
          </fieldset>
          <fieldset>
            <legend>地點類型</legend>
            <ToggleGroup<LocationKind>
              values={LOCATION_OPTIONS}
              selected={filters.locations}
              onChange={(values) => set("locations", values)}
            />
          </fieldset>
          <fieldset className="filters__wide">
            <legend>年齡層</legend>
            <ToggleGroup<string>
              values={AGE_GROUPS.map((label) => ({ value: label, label }))}
              selected={filters.ageGroups}
              onChange={(values) => set("ageGroups", values)}
            />
          </fieldset>
          <fieldset>
            <legend>年齡範圍</legend>
            <div className="range-fields">
              <label className="field"><span>最小</span><input type="number" min="1" max="120" value={filters.ageMin} onChange={(event) => set("ageMin", event.target.value)} /></label>
              <span>—</span>
              <label className="field"><span>最大</span><input type="number" min="1" max="120" value={filters.ageMax} onChange={(event) => set("ageMax", event.target.value)} /></label>
            </div>
          </fieldset>
          <fieldset>
            <legend>累積活動時間（小時）</legend>
            <div className="range-fields">
              <label className="field"><span>最小</span><input type="number" min="0" step="0.5" value={filters.timeMinHours} onChange={(event) => set("timeMinHours", event.target.value)} /></label>
              <span>—</span>
              <label className="field"><span>最大</span><input type="number" min="0" step="0.5" value={filters.timeMaxHours} onChange={(event) => set("timeMaxHours", event.target.value)} /></label>
            </div>
          </fieldset>
          <fieldset>
            <legend>跑步次數</legend>
            <div className="range-fields">
              <label className="field"><span>最小</span><input type="number" min="0" value={filters.activityMin} onChange={(event) => set("activityMin", event.target.value)} /></label>
              <span>—</span>
              <label className="field"><span>最大</span><input type="number" min="0" value={filters.activityMax} onChange={(event) => set("activityMax", event.target.value)} /></label>
            </div>
          </fieldset>
          <fieldset>
            <legend>達標日期</legend>
            <div className="range-fields">
              <label className="field"><span>開始</span><input type="date" value={filters.dateFrom} onChange={(event) => set("dateFrom", event.target.value)} /></label>
              <span>—</span>
              <label className="field"><span>結束</span><input type="date" value={filters.dateTo} onChange={(event) => set("dateTo", event.target.value)} /></label>
            </div>
          </fieldset>
          <fieldset className="filters__wide">
            <legend>日本都道府縣</legend>
            <div className="select-row">
              <select
                value=""
                onChange={(event) => {
                  const value = Number(event.target.value);
                  if (value && !filters.prefectures.includes(value)) set("prefectures", [...filters.prefectures, value]);
                }}
              >
                <option value="">選擇都道府縣…</option>
                {PREFECTURES.map((label, index) => <option key={label} value={index + 1}>{label}</option>)}
              </select>
              <div className="toggle-list">
                {filters.prefectures.map((code) => (
                  <button className="filter-chip is-active" type="button" key={code} onClick={() => set("prefectures", filters.prefectures.filter((value) => value !== code))}>
                    × {PREFECTURES[code - 1]}
                  </button>
                ))}
              </div>
            </div>
          </fieldset>
        </div>
      )}
    </section>
  );
}
